import { Box, Button } from '@hover/blueprint';
import { chakra } from '@hover/blueprint/chakra';
import React from 'react';
import { Trans } from 'react-i18next';
import { Redirect, useHistory } from 'react-router';

import { useProfessionalSignUp } from 'src/modules/app/hooks/useProfessionalSignUp';
import type { ProfileFormData } from 'src/modules/signUp/components/Professional/types';
import { Alert, Link } from 'src/shared/components';
import { Form as SharedForm } from 'src/shared/components/forms';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import type { ConsentArgs } from 'src/shared/graphql/generated';
import {
  useCreateInsuranceUserMutation,
  useCreateProUserMutation,
} from 'src/shared/graphql/generated';
import {
  useClosure,
  useEphemeralEmail,
  useLocation,
  usePartner,
  useTranslate,
} from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route } from 'src/shared/route';
import { Page, accountCreated, identify } from 'src/shared/telemetry';
import { getUtmCookies } from 'src/shared/utils/cookie';
import { isNetworkError } from 'src/shared/utils/graphql';

import { Fields } from '../Fields';
import { formatPhoneNumber } from '../formatPhoneNumber';

import { getTryDifferentEmailRoute } from './getDifferentEmail';
import {
  makeValidationSchema,
  makeValidationSchemaWithBranch,
} from './validationSchema';

const ProfileForm = () => {
  const t = useTranslate(TranslationNamespace.SIGN_UP);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const partner = usePartner();

  const isBeacon = partner?.subdomain === 'beacon';
  const isInsurance = pathname === Route.SIGN_UP_INSURANCE_ORGANIZATION.path;

  const { clear: clearEphemeralEmail } = useEphemeralEmail();
  const { user, userDispatch } = useProfessionalSignUp();

  const submitButtonText = t('finish');

  const [createProUser, { error: proError, loading: proLoading }] =
    useCreateProUserMutation();
  const [
    createInsuranceUser,
    { error: insuranceError, loading: insuranceLoading },
  ] = useCreateInsuranceUserMutation();

  const error = proError || insuranceError;
  const loading = proLoading || insuranceLoading;

  const onSubmit = useClosure(async (formData: ProfileFormData) => {
    const orgData = {
      company: formData.company,
      familyName: formData.familyName,
      givenName: formData.givenName,
      mobilePhone: formatPhoneNumber(formData.mobilePhone) as string,
    };

    userDispatch({
      type: 'UPDATE_PROFILE',
      payload: orgData,
    });

    try {
      if (!('password' in user)) return;

      let consents: ConsentArgs[] | undefined;

      if ('contactPolicy' in user && user.contactPolicy?.policy) {
        // Since this is behind a toggle, it might not always
        // be within the form.
        const { contactPolicy } = user;
        consents = [
          {
            policyId: contactPolicy.policy.id,
            hasConsent: formData.contactPolicy?.hasConsent || false,
            source: 'web-signup',
          },
        ];
      }

      const utmCookies = getUtmCookies();
      let marketingCampaign;
      if (utmCookies.utmCampaign) {
        marketingCampaign = {
          name: utmCookies.utmCampaign,
          source: utmCookies.utmSource,
          medium: utmCookies.utmMedium,
          content: utmCookies.utmContent,
        };
      }

      const userVariables = {
        email: user.email,
        firstName: orgData.givenName,
        lastName: orgData.familyName,
        password: user.password,
        passwordConfirmation: user.password,
        mobilePhone: orgData.mobilePhone,
        consents,
        marketingCampaign,
      };

      let createResponse;
      if (isInsurance) {
        const result = await createInsuranceUser({
          variables: {
            user: userVariables,
            org: {
              name: orgData.company,
            },
          },
        });

        createResponse = result?.data?.createInsuranceUser;
      } else {
        const result = await createProUser({
          variables: {
            user: userVariables,
            org: {
              name: orgData.company,
            },
            parentOrgCode: isBeacon ? formData?.branch : partner?.parentOrgCode,
            signupVersion: user.signupVersion,
          },
        });
        createResponse = result?.data?.createProUser;
      }

      if (createResponse) {
        const {
          jwt: { accessToken, refreshToken },
          user: { email, id, intercomSha },
        } = createResponse;

        try {
          await identify({
            userId: id,
            email,
            intercomSha,
          });

          accountCreated({
            button_or_link: 'button',
            button_or_link_text: submitButtonText,
            page_or_screen_name: isInsurance
              ? Page.INSURANCE_COMPLETE_PROFILE__INPUT
              : Page.COMPLETE_PROFILE__INPUT,
            primary_cta: true,
            platform_type: 'web',
          });

          window.branch.logEvent('COMPLETE_REGISTRATION');

          clearEphemeralEmail();
        } finally {
          void defaultLocalStorageAdapter.setAccessToken(accessToken);
          void defaultLocalStorageAdapter.setRefreshToken(refreshToken);

          const path = isInsurance
            ? Route.SIGN_UP_INSURANCE_SURVEY.path
            : Route.SIGN_UP_PROFESSIONAL_SURVEY.path;

          history.push(path + search);
        }
      }
    } catch {
      // TODO: expected authentication errors should probably be
      // handled in an Apollo error link at the client level
    }
  });

  if (!('email' in user) || !('password' in user)) {
    return <Redirect to={Route.SIGN_UP_PROFESSIONAL.path + search} />;
  }

  const initialValues =
    user && 'company' in user
      ? user
      : {
          company: '',
          familyName: '',
          givenName: '',
          mobilePhone: '',
        };

  const validationSchema = isBeacon
    ? makeValidationSchemaWithBranch(t)
    : makeValidationSchema(t);

  const hasErrorAtIdle = error && !loading;
  const contactPolicy =
    'contactPolicy' in user ? user?.contactPolicy?.policy : null;

  return (
    <SharedForm<ProfileFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      testId="org-form"
      validationSchema={validationSchema}
    >
      <Fields
        contactPolicy={partner ? undefined : contactPolicy}
        showBranchField={isBeacon}
      />
      {hasErrorAtIdle &&
        (isNetworkError(error) ? (
          <Alert alignSelf="stretch" flexDirection="row">
            {t('somethingWentWrongTryAgain')}
          </Alert>
        ) : (
          <>
            <Alert alignSelf="stretch" flexDirection="row">
              <Trans
                components={{
                  '1': (
                    <chakra.span fontStyle="italic" wordBreak="break-word" />
                  ),
                }}
                i18nKey="accountWithEmailAlreadyExists"
                t={t}
                values={{ email: user.email }}
              />
            </Alert>
            <Box alignItems="center" justifyContent="center" marginTop={400}>
              <Link size={400} to={Route.LOGIN.path}>
                {t('logIn')}
              </Link>
              {
                // eslint-disable-next-line i18next/no-literal-string
              }
              &nbsp;&middot;&nbsp;
              <Link size={400} to={getTryDifferentEmailRoute(user, pathname)}>
                {t('tryADifferentEmail')}
              </Link>
            </Box>
          </>
        ))}
      <Button
        alignSelf="stretch"
        isLoading={loading}
        marginTop={500}
        size="large"
        testId="create-account-button"
        type="submit"
      >
        {submitButtonText}
      </Button>
    </SharedForm>
  );
};

export { ProfileForm };
